import { lazy, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { Col, Container, Row } from 'react-bootstrap';
import { v5 as uuidv5 } from 'uuid';
import { FindADoctorAd } from '@medifind/ads';
import { MediFindMoments } from '@medifind/ads';
import { ConditionRollups } from '@medifind/codings';
import {
  useLazyComponentPreload,
  useResultLimit,
  useResultRouteQueryResponse,
  useUrlPathLocation,
  useUrlQueryLocationString,
} from '@medifind/hooks';
import { useBookmarkLoader, usePermalinkLoader, useSearchPageBookmarkPermalink } from '@medifind/hooks';
import { IconSecondOpinion } from '@medifind/icons';
import { doctorSearchResults, getConditionRollups } from '@medifind/interface';
import { usePageChanger, usePageHooks, useRouter, useSortChanger } from '@medifind/router';
import { DoctorCard, DoctorFiltersGroupV2, FilterSideContainer, Grid } from '@medifind/search-results';
import { BreadcrumbLinks, SEO } from '@medifind/seo';
import {
  InsuranceNotKnownDisclaimer,
  LearnTiers,
  MFLink,
  SchedulesOnlineDisclaimer,
  TiersExplanation,
  getStaticFeatureFlag,
  useInsuranceNotKnownDisclaimerPosition,
  useSchedulesOnlineDisclaimerPosition,
} from '@medifind/shared-basic-components';
import { Tiers } from '@medifind/shared-basic-components';
import { MFLazyLoad } from '@medifind/shared-basic-components';
import { LastUpdate } from '@medifind/shared-basic-components';
import { isPrerender, useMediaQueryUp } from '@medifind/utils';
import { getValueMediaBreakpointDown } from '@medifind/utils';
import { getNullList } from '@medifind/utils';
import { fillTemplate } from '@medifind/utils';
import {
  clearDoctorFilters,
  doctorFiltersDependencies,
  logEventGA4,
  useDoctorAvailabilityFilter,
  useDoctorGenderFilter,
  useDoctorInsuranceFilter,
  useDoctorLanguageFilter,
  useDoctorSearchRadiusFilter,
  useDoctorSecondarySpecialtyFilter,
  useDoctorSpecialtyFilter,
  useDoctorTierFilter,
  useDoctorYearsExpFilter,
  useFilterContext,
  useLocationStore,
  withFilters,
} from '@medifind/zustand';
import { updateDoctorSpecialtyFilterOption } from '@medifind/zustand';
import { selectRouteParams, useRouteStore } from '@medifind/zustand';
import { DoctorsGridDisclaimer } from '../../components/disclaimers';
import styles from './doctors.module.scss';
const DoctorResultFilterList = lazy(() => import('@medifind/lazy-loadable/lib/search-results/LazyDoctorResultFilterList'));
const ByLocationWidget = lazy(() => import('@medifind/lazy-loadable/lib/shared-basic-components/LazyByLocationWidget'));
const ProgrammaticFAQ = lazy(() => import('./programatic-faqs'));

const SecondOpinionElement = ({ inline, projectId }) => {
  if (inline) {
    return (
      <MFLink to={`/second-opinion/search?projectId=${projectId}`}>
        <div className={styles['second-opinion__button__container']}>
          <div className={styles['second-opinion__button__element']}>Find a second opinion</div>
        </div>
      </MFLink>
    );
  } else {
    return (
      <MFLink to={`/second-opinion/search?projectId=${projectId}`} className={styles['second-opinion__container']}>
        <div className={styles['second-opinion__icon']}>
          <IconSecondOpinion
            width={88}
            height={88}
            showBoxShadow={false}
            style={{
              width: '56px !important',
              minWidth: '56px !important',
              height: '56px !important',
              background: 'none !important',
            }}
          />
        </div>
        <div className={styles['second-opinion__text']}>
          <div className={styles['second-opinion__text--top']}>Find a second opinion</div>

          <div className={styles['second-opinion__button']}>
            <span className={styles['second-opinion__button--text']}>Find now</span>
          </div>
        </div>
      </MFLink>
    );
  }
};

const sortItems = [
  { display: 'Relevance', value: 'relevance', isDefault: true },
  { display: 'Distance', value: 'distance' },
  { display: 'Expertise', value: 'rating' },
];

/* __ROUTE_PATH__=/(conditions|procedures)/:codingSlug/:projectId/doctors */
/* __ROUTE_PATH__=/(conditions|procedures)/:codingSlug/:projectId/doctors/location/:countryCode?/:stateProvinceCode?/:city? */
/* __ROUTE_PATH__=/(conditions|procedures)/:codingSlug/:projectId/doctors/drug/:drugId */
const DoctorsPage = (props) => {
  usePageHooks();
  const filters = useFilterContext();
  const router = useRouter();

  const {
    query,
    location: routerLocation,
    pathParams: { countryCode: pathCountryCode, stateProvinceCode: pathStateProvinceCode, city },
  } = router;
  const { projectId, codingSlug, drugId } = query;
  const { codingName, drugName, codingType, isRareCondition } = useRouteStore(selectRouteParams);
  useLazyComponentPreload([DoctorResultFilterList]);
  const [size] = useState(20);
  const [showTiers, setShowTiers] = useState(false);
  const location = useLocationStore();
  const { urlLocation, isLoading: isUrlLocationLoading, invalidLocation } = useUrlPathLocation();
  useUrlQueryLocationString();
  const isSmallDesktopAndAbove = useMediaQueryUp('lg');

  const { data: conditionRollupData } = useQuery({
    queryKey: ['conditionRollups', projectId],
    queryFn: async () => {
      try {
        return await getConditionRollups({ projectId });
      } catch (e) {
        return {};
      }
    },
    enabled: !!projectId,
    select: (response) => response?.results,
  });

  const { sort, handleSortChange, changeSortManualURLModification } = useSortChanger('sort', sortItems.find((x) => x.isDefault).value);
  const { page, handlePageChange } = usePageChanger('page', [...filters.valueDependencies, projectId, location, sort]);
  usePermalinkLoader({
    onLoad: ({ data: { sort } }) => changeSortManualURLModification(sort),
    filterList: filters.filterList,
  });
  useBookmarkLoader({
    onLoad: ({ data: { sort } }) => changeSortManualURLModification(sort),
    filterList: filters.filterList,
  });

  useEffect(() => {
    if (query.specialty && !filters.values.doctorSpecialty) {
      updateDoctorSpecialtyFilterOption({
        value: query.specialty.toLowerCase(),
        display: query.specialty[0].toUpperCase() + query.specialty.slice(1).toLowerCase(),
      });
    }
    if (location.fidelity > 3 && sort === 'distance') {
      handleSortChange('relevance');
    }
  }, []);

  useEffect(() => {
    filters.setFilterDependencies([codingSlug, query.specialty], { exclude: query.specialty ? ['doctorSpecialty'] : [] });
  }, [query.specialty, codingSlug]);

  const { resultLimitAchieved, resultLimitMessage } = useResultLimit({ page, size });

  const filterDepsChange = filters.checkFilterDependencies([codingSlug, query.specialty]);
  const locationProvided = pathCountryCode || pathStateProvinceCode;
  const locationChange = (locationProvided && urlLocation && !isUrlLocationLoading) || !locationProvided;

  const {
    doctorSpecialty,
    doctorSecondarySpecialty,
    doctorSearchRadius,
    doctorGender,
    doctorYearsExperience,
    doctorLanguage,
    doctorTier,
    doctorAvailability,
    doctorInsurance,
  } = filters.values;
  const { error, notFound, unauthorized, results, featuredResults, totalResults, scoresCount } = useResultRouteQueryResponse({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'conditionSearch',
      ...filters.dependencies,
      page,
      sort,
      codingSlug,
      query.specialty,
      location,
      projectId,
      size,
      drugId,
    ].filter(Boolean),
    queryFn: async ({ signal }) => {
      const { countryCode, lon, lat, stateProvinceCode, fidelity } = location;
      const query = {
        specialty: [doctorSpecialty, doctorSecondarySpecialty].filter(Boolean),
        projectId,
        radius: doctorSearchRadius || null,
        lat,
        lon,
        country: countryCode || null,
        state: stateProvinceCode || null,
        fidelity,
        size,
        page,
        drugId,
        gender: doctorGender || null,
        doctorYearsExperience: doctorYearsExperience,
        languages: doctorLanguage,
        doctorTier: doctorTier,
        sort: sort,
        type: 'conditionSearch',
        showFeaturedCards: true,
        telemedicine: doctorAvailability.includes('telemedicine'),
        acceptsNewPatients: doctorAvailability.includes('acceptsNewPatients'),
        appointmentAssistOptIn: doctorAvailability.includes('appointmentAssistOptIn'),
        insurances: doctorInsurance,
      };
      return doctorSearchResults(query, signal);
    },
    enabled: !!(filterDepsChange && projectId && locationChange),
    onError: (error) => {
      if (error !== null) {
        logEventGA4(
          'exception',
          { type: 'doctors', method: error.code, description: error.message, item_id: projectId || drugId },
          uuidv5(`doctors_conditions${projectId ? `_${projectId}` : ''}`, '00000000-0000-0000-0000-000000000000'),
        );
      }
    },
  });

  const schedulesOnlineDisclaimerPosition = useSchedulesOnlineDisclaimerPosition({ featuredResults, results });
  const insuranceNotKnownDisclaimerPosition = useInsuranceNotKnownDisclaimerPosition({ featuredResults, results });

  const toggleTiers = () => setShowTiers(!showTiers);

  useSearchPageBookmarkPermalink(
    `${codingName} Doctors`,
    'find-doctor',
    {
      drugId,
      projectId,
      urlLocation: { countryCode: pathCountryCode, stateProvinceCode: pathStateProvinceCode, city },
    },
    {
      drugName: drugName,
    },
    filters.filterList,
    [...filters.dependencies, pathCountryCode, pathStateProvinceCode, city],
  );

  // Filter sort options
  const validSortItems = useMemo(() => {
    let tempSortItems = sortItems;
    tempSortItems = location.fidelity > 3 ? tempSortItems.filter(({ value }) => value !== 'distance') : tempSortItems;
    return tempSortItems;
  }, [location.fidelity]);

  // If the sort is no longer valid, change it
  useEffect(() => {
    if (!validSortItems.find((s) => s.value === sort)) handleSortChange(validSortItems[0].value);
  }, [validSortItems]);

  const pageTitle = fillTemplate(
    'The 20 Best [codingName:[codingName]] Doctors [!mediumLocationString: Near Me][city: near][mediumLocationString&!city: in][mediumLocationString: [mediumLocationString]]',
    {
      ...urlLocation,
      codingName,
    },
  );
  const descriptionCounts = scoresCount
    ?.filter((d) => d.label)
    ?.reduce((acc, { label, value }, i, arr) => {
      return acc + (i != 0 ? (i < arr.length - 1 ? ', ' : ' and ') : '') + `${value} are ${label}`;
    }, '');
  const description = `MediFind found ${totalResults} doctor${totalResults === 1 ? '' : ''} with experience in ${codingName}${
    urlLocation?.mediumLocationString ? ' near ' + urlLocation?.mediumLocationString : ''
  }.${descriptionCounts ? ` Of these, ${descriptionCounts}.` : ''}`;

  if (error || ((pathCountryCode || pathStateProvinceCode) && invalidLocation)) {
    throw new Error(404);
  }
  return (
    <>
      {codingName && pageTitle && (
        <SEO
          title={`${pageTitle} | MediFind`}
          description={description}
          canonical={`${routerLocation.origin}${routerLocation.pathname}`}
          noIndex={totalResults === 0}
        />
      )}

      <Container>
        <Row>
          <Col lg={4} xl={3} className="hide--md">
            <FilterSideContainer
              title={<>Refine your search results with&nbsp;filters</>}
              lazyLoadOptions={{ height: 600, resize: true }}
              clearFilters={clearDoctorFilters}
            >
              <DoctorResultFilterList />
            </FilterSideContainer>
            {isSmallDesktopAndAbove && (
              <MediFindMoments type="scrolling-rectangle" targeting={{ pageType: 'condition-provider-search', position: 'left1' }} />
            )}
            {results && <LastUpdate postion={'top'} resultsLoaded={!!results} dateModify={results[0]?.dateModify} />}
            <FindADoctorAd />
            <LearnTiers showTiers={showTiers} toggleTiers={toggleTiers} />
            {(showTiers || isPrerender) && <Tiers className={styles['tiers__explain']} />}
            {projectId && <SecondOpinionElement projectId={projectId} inline={false} />}
          </Col>
          <Col sm={12} lg={8} xl={9} className={styles['outer__col']}>
            <h2 className={'hide-h-tag'}>{description}</h2>
            <div className={styles['results__container']}>
              <DoctorFiltersGroupV2
                sortItems={validSortItems}
                onSortByChange={({ value }) => handleSortChange(value)}
                sortValue={sort}
                totalResults={totalResults}
                classes={{
                  filters: styles['filters--override'],
                  sortContainer: styles['sort--override'],
                }}
                smallAt="md"
                projectId={projectId}
                adOptions={{ pageType: 'condition-provider-search' }}
              />

              <Grid
                error={error || resultLimitAchieved}
                errorMessage={resultLimitMessage}
                maxColumnCount={1}
                lazyLoad={{
                  offset: 100,
                  height: getValueMediaBreakpointDown({ xs: 448, sm: 448, md: 244, default: 244 }),
                  noSuspense: true,
                }}
                items={((conditionRollupData != null && featuredResults) || [])
                  .map((doctor, i) => (
                    <DoctorCard
                      key={`codings-doctor-feat-${i}`}
                      doctor={doctor}
                      showScore={false}
                      showConditionCount={false}
                      {...{ projectId, codingSlug, codingName, codingType }}
                      priority={3}
                      showPhoneButton={true}
                      smallAt={['xs', 'sm', 'lg']}
                    />
                  ))
                  .concat(
                    ((conditionRollupData != null && results) || getNullList(size)).map((doctor, i) => (
                      <DoctorCard
                        key={`codings-doctor-${i}`}
                        doctor={doctor}
                        showScore={!!projectId}
                        showConditionCount={!projectId}
                        {...{ projectId, codingSlug, codingName, codingType }}
                        priority={3}
                        showPhoneButton={true}
                        smallAt={['xs', 'sm', 'lg']}
                      />
                    )),
                  )}
                noResultsHtml={
                  conditionRollupData?.foundResults && <ConditionRollups data={conditionRollupData} totalResults={totalResults} />
                }
                insertResult={[
                  ...[2, 6, 10, 14, 18].map((n, i) => ({
                    renderAfter: n,
                    render: (
                      <>
                        {n === 2 && !isSmallDesktopAndAbove ? (
                          <MediFindMoments
                            type="scrolling-rectangle"
                            targeting={{ pageType: 'condition-provider-search', position: `mid${i + 1}` }}
                          />
                        ) : n > 2 ? (
                          <MediFindMoments
                            type="scrolling-rectangle"
                            targeting={{ pageType: 'condition-provider-search', position: `mid${!isSmallDesktopAndAbove ? i + 1 : i}` }}
                          />
                        ) : (
                          <></>
                        )}
                      </>
                    ),
                    dontShowAtEnd: i === 0 ? false : true,
                  })),
                  {
                    renderAfter: 2,
                    render: (
                      <>
                        <TiersExplanation />
                      </>
                    ),
                    dontShowAtEnd: false,
                  },
                  {
                    renderAfter: 3,
                    render: (
                      <>
                        {totalResults >= 10 && conditionRollupData?.foundResults && (
                          <ConditionRollups data={conditionRollupData} totalResults={totalResults} />
                        )}
                        <div className={styles['inline-buttons']}>
                          <LearnTiers inline showTiers={showTiers} toggleTiers={toggleTiers} />
                        </div>
                        {showTiers && <Tiers className={classNames(styles['tiers__explain'], styles['tiers__explain--mobile'])} />}
                      </>
                    ),
                    dontShowAtEnd: false,
                  },
                  {
                    renderAfter: 10,
                    render: (
                      <>
                        {totalResults < 10 && conditionRollupData?.foundResults && (
                          <ConditionRollups data={conditionRollupData} totalResults={totalResults} />
                        )}
                      </>
                    ),
                    dontShowAtEnd: false,
                  },
                  {
                    renderBefore: schedulesOnlineDisclaimerPosition + 1,
                    render: <SchedulesOnlineDisclaimer />,
                    dontShowAtEnd: true,
                  },
                  {
                    renderBefore: insuranceNotKnownDisclaimerPosition + 1,
                    render: <InsuranceNotKnownDisclaimer />,
                    dontShowAtEnd: true,
                  },
                ]}
                id={'doctors-list'}
                size={size}
                pagination={{
                  page,
                  totalResults,
                  onPageChange: handlePageChange,
                  type: 'eventWithRouterShallowChange',
                  forceScroll: true,
                  wideLayout: true,
                  requireLogin: {
                    errorMessage: `Sign in or create a free MediFind account to see more ${codingName} experts. View, save, and contact these experts to find the care you deserve.`,
                    errorTitle: `More Expertise Awaits`,
                    afterPage: !!drugId || !!pathCountryCode ? 1 : 5,
                  },
                }}
                noBorder
                addGutters
                bottomDisclaimer={results?.length ? <DoctorsGridDisclaimer /> : null}
              />
            </div>
            {results && <LastUpdate postion={'bottom'} resultsLoaded={!!results} dateModify={results[0]?.dateModify} />}
          </Col>
        </Row>
      </Container>
      <MFLazyLoad height={6000} offset={400} checkOnChange={results && totalResults < 3}>
        <ProgrammaticFAQ {...{ urlLocation }} />
      </MFLazyLoad>
      <MFLazyLoad height={600} offset={400}>
        <ByLocationWidget
          baseUrl={`/conditions/${codingSlug}/${projectId}/doctors/location`}
          title={`More ${codingName} Doctors by ${urlLocation?.stateProvinceCode ? 'city' : 'state'}`}
        />
      </MFLazyLoad>
      <Container>
        <Row>
          <Col>
            <BreadcrumbLinks
              items={[
                { name: 'Homepage', link: '/' },
                ...[
                  projectId
                    ? {
                        name: `${codingName} Doctors ${!urlLocation?.locationString ? ' Near Me' : ''}`,
                        link: `/conditions/${codingSlug}/${projectId}/doctors`,
                      }
                    : null,
                ],
                // should always follow path params, not location filter
                ...[urlLocation?.locationString ? { name: urlLocation.locationString, link: router.wouterLocation } : null],
              ]}
            />
          </Col>
        </Row>
      </Container>
      {/* REVIEW get rid of this? */}
      <div className={styles['second-opinion-footer']}>
        <div className={styles['text']}>Already have a doctor?</div>
        <div>
          {projectId && (
            <MFLink to={`/second-opinion/search?projectId=${projectId}`} className={styles['button']}>
              <IconSecondOpinion width={52} height={52} showBoxShadow={false} />
              <span className={styles['button-text']}>Find a second opinion</span>
            </MFLink>
          )}
        </div>
      </div>
    </>
  );
};

export default withFilters(
  [
    useDoctorGenderFilter,
    useDoctorYearsExpFilter,
    useDoctorTierFilter,
    useDoctorSpecialtyFilter,
    useDoctorSecondarySpecialtyFilter,
    useDoctorLanguageFilter,
    useDoctorSearchRadiusFilter,
    useDoctorAvailabilityFilter,
    ...(getStaticFeatureFlag('INSURANCE_FILTER') ? [useDoctorInsuranceFilter] : []),
  ],
  [doctorFiltersDependencies],
  [clearDoctorFilters],
)(DoctorsPage);
